import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import classes from "./Timeline.module.css";

type Props = {
  /** Horizontal anchor inside the plot, 0–100. */
  leftPct: number;
  /** The top of the hovered mark, in px from the plot's top. The card opens
   *  above it. */
  anchorY: number;
  children: ReactNode;
};

// Space between the card and the hovered mark.
const TOOLTIP_GAP = 6;
// Above MUI's Dialog (1300), which hosts the full-screen timeline.
const TOOLTIP_Z_INDEX = 1400;

/**
 * The hover card every Timeline row uses. It opens **above** its anchor,
 * centred, and hangs left or right near the plot's edges so it never spills
 * out of the card.
 *
 * The anchor is an empty, absolutely positioned node inside the plot; the
 * card itself is portalled to `document.body` at the anchor's viewport
 * position. Rendered in place it would be clipped by any scroll container
 * around the rows (the full-screen tests and services scroller) and, opening
 * below, would grow that container and jump the page.
 */
const TimelineTooltip = ({ leftPct, anchorY, children }: Props) => {
  const theme: Theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const translateX = leftPct > 80 ? "-100%" : leftPct < 20 ? "0" : "-50%";

  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setPosition({ x: rect.left, y: rect.top });
    };
    measure();
    // Scrolling under a still pointer (page or scroller) moves the anchor.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [leftPct, anchorY]);

  return (
    <>
      <div
        ref={anchorRef}
        style={{
          position: "absolute",
          left: `${leftPct}%`,
          top: anchorY - TOOLTIP_GAP,
          width: 0,
          height: 0,
        }}
      />
      {position &&
        createPortal(
          <div
            className={classes.tooltip}
            style={{
              ...styles.container(theme),
              position: "fixed",
              left: position.x,
              top: position.y,
              transform: `translate(${translateX}, -100%)`,
              zIndex: TOOLTIP_Z_INDEX,
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
};

/** One entry inside a tooltip: a bold title and one or two lines under it. */
export const TooltipEntry = ({
  title,
  lines,
  boxed = false,
}: {
  title: string;
  lines: string[];
  boxed?: boolean;
}) => {
  const theme: Theme = useTheme();
  return (
    <div style={boxed ? styles.boxed(theme) : undefined}>
      <div style={styles.title(theme)}>{title}</div>
      {lines.map((line) => (
        <div key={line} style={styles.line(theme)}>
          {line}
        </div>
      ))}
    </div>
  );
};

export default TimelineTooltip;

const styles = {
  container: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  boxed: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "4px",
    padding: "4px 6px",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  line: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
